import * as Sentry from '@sentry/nextjs';
import * as _ from 'lodash';

import { APIRequestLabel, ValidationConfig } from '../consts';
import { callHttpDelete, callHttpGet, callHttpPatch, callHttpPost, callHttpPut } from '../http';
import {
  BlockCreateModel,
  BlockModel,
  BlockType,
  BlockUpdateModel,
  TaskCreateModel,
  TaskModel,
  TaskUpdateModel,
  VersionedEntityType
} from '../models';

const cleanupVersionedEntity = (e: any): any => {
  delete e.entityId;
  delete e.versionType;
  delete e.operationType;
  delete e.createdAt;
  delete e.updateAt;
  // legacy
  delete e.id;
  delete e.cid;

  return e;
};

const cleanupTask = (t: TaskCreateModel | TaskModel): TaskCreateModel => {
  let taskCopy = _.cloneDeep(t);
  cleanupVersionedEntity(taskCopy);
  delete taskCopy.tags;
  taskCopy.blocks = (taskCopy.blocks || []).map(cleanupBlock);

  return taskCopy;
};

const cleanupBlock = (
  b: BlockCreateModel | BlockUpdateModel
): BlockCreateModel | BlockUpdateModel => {
  let blockCopy = _.cloneDeep(b);
  cleanupVersionedEntity(blockCopy);
  delete blockCopy.tags;
  // TODO: for now we're not supporting exerciseId: https://app.clickup.com/t/8693upf1t
  delete blockCopy.exerciseId;

  // TODO: for now we're not support supplyQuantities: https://app.clickup.com/t/8693upf1x
  delete blockCopy.supplyQuantities;

  // clean up content
  if (blockCopy.content) {
    blockCopy.content = {
      ...blockCopy.content,
      item: {
        ...blockCopy.content.item,
        mediaitem: null
      },
      // Delete mediaitem from attachments
      attachments: blockCopy.content.attachments
        ? blockCopy.content.attachments.map(({ mediaitem, ...rest }) => rest)
        : blockCopy.content.attachments
    };
    delete blockCopy.content.item.mediaitem;
  }

  if (blockCopy.content?.choices) {
    blockCopy.content.choices = blockCopy.content.choices.map((c) => {
      let out = {
        ...c,
        item: {
          ...c.item,
          mediaitem: null
        }
      };
      delete out.item.mediaitem;
      return out;
    });
  }

  if (blockCopy.content?.bricks) {
    blockCopy.content.bricks = blockCopy.content.bricks.map((brick) => {
      let out = {
        ...brick,
        item: {
          ...brick.item,
          mediaitem: null
        }
      };
      delete out.item.mediaitem;
      return out;
    });
  }

  if (blockCopy.content?.problems) {
    blockCopy.content.problems = blockCopy.content.problems.map((problem) => ({
      ...problem,
      item: {
        ...problem.item,
        mediaitem: null
      }
    }));
    blockCopy.content.problems.forEach((problem) => {
      delete problem.item.mediaitem;
    });
  }

  if (blockCopy.content?.flashcards) {
    blockCopy.content.flashcards = blockCopy.content.flashcards.map((card: any) => {
      let out = {
        ...card,
        item: {
          ...card.item,
          mediaitem: null
        }
      };
      delete out.item.mediaitem;
      return out;
    });
  }

  blockCopy.groupBlocks = (blockCopy.groupBlocks || []).map(cleanupBlock);

  return blockCopy;
};

const getTask = (get, set) => {
  return async (
    taskEntityId: string,
    params?: { maxVersionType?: VersionedEntityType }
  ): Promise<TaskModel | undefined> => {
    params = params || {};

    let qParams = {};
    if (params.maxVersionType) {
      qParams['maxVersionType'] = params.maxVersionType;
    }
    let q = new URLSearchParams(qParams).toString();

    const getApiCallStatus = await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_TASK,
      `/tasks/${taskEntityId}?${q}`
    );
    if (getApiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(getApiCallStatus.error));
      return undefined;
    }
    return getApiCallStatus.response;
  };
};

const getTasks = (get, set) => {
  return async (params?: {
    goalId?: number;
    maxVersionType?: VersionedEntityType;
    query?: string;
    withBlocks?: boolean;
  }) => {
    params = params || {};
    let qParams = {};
    if (params.maxVersionType) {
      qParams['maxVersionType'] = params.maxVersionType;
    }
    if (params.goalId) {
      qParams['goalId'] = params.goalId;
    }

    if (params.query) {
      qParams['query'] = params.query;
    }

    if (params.withBlocks === false) {
      qParams['withBlocks'] = false;
    }

    let q = new URLSearchParams(qParams).toString();

    await callHttpGet(get, set, APIRequestLabel.GET_TASKS, `/tasks?${q}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_TASKS];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      tasks: apiCallStatus.response?.tasks
    });
  };
};

const buildClonedTaskTitle = (titlePrefix: string, title: string): string =>
  `${titlePrefix} ${title}`.slice(0, ValidationConfig.TASK__TITLE__MAX_LENGTH);

const cloneTask = (get, set) => {
  return async (taskEntityId: string, titlePrefix: string): Promise<string | undefined> => {
    const taskToClone = await getTask(get, set)(taskEntityId, {
      maxVersionType: VersionedEntityType.DRAFT
    });
    if (!taskToClone) {
      return;
    }
    const taskCloneData = cleanupTask(taskToClone);
    taskCloneData.title = buildClonedTaskTitle(titlePrefix, taskCloneData.title);

    await callHttpPost(get, set, APIRequestLabel.CLONE_TASK, `/tasks`, taskCloneData);
    const apiCallStatus = get().apiCallStatus[APIRequestLabel.CLONE_TASK];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    const clonedEntityId = apiCallStatus.response.entityId;

    const clonedTask = await getTask(get, set)(clonedEntityId, {
      maxVersionType: VersionedEntityType.DRAFT
    });
    if (!clonedTask) {
      return;
    }

    const idx = get().tasks.findIndex((t) => t.entityId === taskToClone.entityId);
    set({
      tasks: [...get().tasks.slice(0, idx + 1), clonedTask, ...get().tasks.slice(idx + 1)]
    });

    return clonedEntityId;
  };
};

const createTask = (get, set) => {
  return async (task: TaskCreateModel): Promise<string | undefined> => {
    await callHttpPost(get, set, APIRequestLabel.CREATE_TASK, `/tasks`, cleanupTask(task));
    const apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_TASK];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response.entityId;
  };
};

const updateTask = (get, set) => {
  return async (taskEntityId: string, task: TaskUpdateModel): Promise<boolean | undefined> => {
    const apiCallStatus = await callHttpPut(
      get,
      set,
      APIRequestLabel.UPDATE_TASK,
      `/tasks/${taskEntityId}`,
      task
    );
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return true;
  };
};

const deleteTask = (get, set) => {
  return async (taskEntityId: string) => {
    await callHttpDelete(get, set, APIRequestLabel.DELETE_TASK, `/tasks/${taskEntityId}`);
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.DELETE_TASK];
    if (apiCallStatus.error) {
      return Promise.reject(apiCallStatus.error);
    }

    set({
      tasks: get().tasks.filter((t) => t.entityId !== taskEntityId)
    });
  };
};

const createBlock = (get, set) => {
  return async (taskEntityId: string, block: BlockCreateModel): Promise<string | undefined> => {
    const apiCallStatus = await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_TASK_BLOCK,
      `/tasks/${taskEntityId}/blocks`,
      cleanupBlock(block)
    );

    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response.entityId;
  };
};

const getBlock = (get, set) => {
  return async (taskEntityId: string, blockEntityId: string): Promise<BlockModel | undefined> => {
    const getApiCallStatus = await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_TASK_BLOCK,
      `/tasks/${taskEntityId}/blocks/${blockEntityId}?maxVersionType=${VersionedEntityType.DRAFT}`
    );
    if (getApiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(getApiCallStatus.error));
      return undefined;
    }
    return getApiCallStatus.response;
  };
};

const getBlockExercises = (get, set) => {
  return async (blockType: BlockType): Promise<void> => {
    // specs are static so make it lazy
    // for now we're using such a simple caching strategy, since we know that the
    // exercises list is static
    const exs = get().blockExercises[blockType];
    if (exs) {
      return exs;
    }

    const params = { blockType: blockType };
    let q = new URLSearchParams(params).toString();
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_TASK_BLOCK_EXERCISES,
      `/tasks/blocks/exercises?${q}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_TASK_BLOCK_EXERCISES];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      blockExercises: {
        ...get().blockExercises,
        [blockType]: apiCallStatus.response.exercises
      }
    });
  };
};

const getBlockAssertionSpecs = (get, set) => {
  return async (blockType: BlockType): Promise<void> => {
    // specs are static so make it lazy
    // for now we're using such a simple caching strategy, since we know that the
    // assertion specs list is static
    const sps = get().blockAssertionSpecs[blockType];
    if (sps) {
      return sps;
    }

    const params = { blockType: blockType };
    let q = new URLSearchParams(params).toString();
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_TASK_BLOCK_ASSERTION_SPECS,
      `/tasks/blocks/assertion_specs?${q}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_TASK_BLOCK_ASSERTION_SPECS];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({
      blockAssertionSpecs: {
        ...get().blockAssertionSpecs,
        [blockType]: apiCallStatus.response.assertionSpecs
      }
    });
  };
};

const cloneBlock = (get, set) => {
  return async (taskEntityId: string, block: BlockCreateModel): Promise<string | undefined> => {
    const apiCallStatus = await callHttpPost(
      get,
      set,
      APIRequestLabel.CLONE_TASK_BLOCK,
      `/tasks/${taskEntityId}/blocks`,
      cleanupBlock(block)
    );
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response.entityId;
  };
};

const updateBlock = (get, set) => {
  return async (
    taskEntityId: string,
    blockEntityId: string,
    block: BlockUpdateModel
  ): Promise<boolean | null | undefined> => {
    const patchState = await callHttpPatch(
      get,
      set,
      APIRequestLabel.UPDATE_TASK_BLOCK,
      `/tasks/${taskEntityId}/blocks/${blockEntityId}`,
      cleanupBlock(block)
    );
    if (patchState.error) {
      Sentry.captureMessage(JSON.stringify(patchState.error));
      //FIXME: https://app.clickup.com/t/869cmf5fj
      return;
    }

    const patchResponse = patchState.response;

    // API returned 200 but operation failed — don't update local versioning fields
    if (!patchResponse?.success) {
      Sentry.captureMessage(
        `updateBlock rejected by the API for task ${taskEntityId} block ${blockEntityId}`
      );
      return null;
    }

    return true;
  };
};

const deleteBlock = (get, set) => {
  return async (taskEntityId: string, blockEntityId: string): Promise<boolean> => {
    const apiCallStatus = await callHttpDelete(
      get,
      set,
      APIRequestLabel.DELETE_TASK_BLOCK,
      `/tasks/${taskEntityId}/blocks/${blockEntityId}`
    );
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return false;
    }
    return true;
  };
};

export {
  buildClonedTaskTitle,
  cleanupBlock,
  cloneBlock,
  cloneTask,
  createBlock,
  createTask,
  deleteBlock,
  deleteTask,
  getBlock,
  getBlockAssertionSpecs,
  getBlockExercises,
  getTask,
  getTasks,
  updateBlock,
  updateTask
};
